import { useAddress } from '@/store/selector';
import { storage } from '@/utils/storage';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import useSWR from 'swr';
import { api, CampaignResponse } from '../api';

const campaignsFetcher = () => api.getCampaigns(3, true);

const campaignFetcher = ([, campaignId, walletAddress]: [
    string,
    string,
    string
]) => api.getCampaign(campaignId, walletAddress);

export function useTradingCampaigns() {
    return useSWR('tradingCampaigns', campaignsFetcher);
}

export const useTradingCampaignVolume = () => {
    const walletAddress = useAddress();

    const {
        data: campaignsData,
        error: campaignsError,
        isLoading: campaignsIsLoading,
        mutate,
    } = useTradingCampaigns();

    const volumeCampaignInfo = campaignsData?.campaigns.find(
        (c) => c.campaign.id === 'd34f8a5b-e886-4d1f-aa57-44217a6177df'
    );
    const volumeCampaignId = volumeCampaignInfo?.campaign.id;

    const {
        data: volumeCampaignData,
        error: volumeCampaignError,
        isLoading: volumeCampaignIsLoading,
    } = useSWR<CampaignResponse>(
        volumeCampaignId
            ? ['volumeCampaign', volumeCampaignId, walletAddress]
            : null,
        campaignFetcher
    );

    const milestones = volumeCampaignData?.campaign_tasks
        .sort((a, b) => {
            const prA = Number(a?.priority || 0);
            const prB = Number(b?.priority || 0);

            // 1️⃣ Sort priority giảm dần
            if (prA !== prB) return prB - prA;

            // 2️⃣ Nếu priority bằng nhau → sort created_at (mới hơn lên trước)
            const cA = dayjs(a?.created_at).valueOf();
            const cB = dayjs(b?.created_at).valueOf();

            return cB - cA; // mới hơn đứng trước
        })
        .map((campaign) => ({
            ...volumeCampaignData.user_tasks.find(
                (t) => t.task_id === campaign.id
            ),
            info: campaign,
        }));

    const currentVolume = useMemo(() => {
        if (!volumeCampaignData?.user_tasks) {
            return 0;
        }
        return Math.max(
            ...volumeCampaignData.user_tasks.map((item) => item.progress)
        );
    }, [volumeCampaignData?.user_tasks]);

    return {
        data: volumeCampaignData,
        currentVolume,
        isLoading: campaignsIsLoading || volumeCampaignIsLoading,
        error: campaignsError || volumeCampaignError,
        milestones,
        mutate,
    };
};

export const useTradingCampaignDailyStreak = () => {
    const walletAddress = useAddress();

    const {
        data: campaignsData,
        error: campaignsError,
        isLoading: campaignsIsLoading,
    } = useTradingCampaigns();

    const dailyStreakCampaignInfo = campaignsData?.campaigns.find(
        (c) => c.campaign.id === '784b1178-0980-4911-998b-fede4f2dbb96'
    );

    const dailyStreakCampaignId = dailyStreakCampaignInfo?.campaign.id;

    const {
        data: dailyStreakCampaignData,
        error: dailyStreakCampaignError,
        isLoading: dailyStreakCampaignIsLoading,
        mutate,
    } = useSWR<CampaignResponse>(
        dailyStreakCampaignId
            ? ['dailyStreakCampaign', dailyStreakCampaignId, walletAddress]
            : null,
        campaignFetcher
    );

    const milestones = useMemo(
        () => dailyStreakCampaignData?.user_tasks,
        [dailyStreakCampaignData?.user_tasks]
    );

    const dataDailyStreak = useMemo(() => {
        return dailyStreakCampaignData?.campaign_tasks
            .map((campaign) => {
                const user_task = dailyStreakCampaignData.user_tasks.find(
                    (t) => t.task_id === campaign.id
                );
                const cachedClaimed = storage.getWithExpiry<boolean>(
                    `campaign_volume_claimed_${user_task?.id}`
                );

                if (dailyStreakCampaignData.task_reset) {
                    storage.remove(`campaign_volume_claimed_${user_task?.id}`);

                    return {
                        ...user_task,
                        info: campaign,
                        isClaimed: false,

                        isCompleted: false,
                        isDisplayReward: !!campaign?.task_params.milestone,
                        isDisplayClaim: false,
                    };
                }

                return {
                    ...user_task,
                    info: campaign,
                    isClaimed:
                        cachedClaimed === true || user_task?.status === 5,
                    isCompleted: user_task?.status === 3,
                    isDisplayReward: !!campaign?.task_params.milestone,
                    isDisplayClaim:
                        user_task?.status === 3 &&
                        !!campaign?.task_params.milestone &&
                        !cachedClaimed,
                };
            })
            .sort((a, b) => {
                const prA = Number(a?.info?.priority || 0);
                const prB = Number(b?.info?.priority || 0);

                // 1️⃣ So sánh priority trước
                if (prB !== prA) return prB - prA;

                // 2️⃣ Nếu priority bằng nhau → so sánh updated_at
                const tA = dayjs(a?.info?.created_at).valueOf();
                const tB = dayjs(b?.info?.created_at).valueOf();

                // updated_at mới hơn đứng trước
                if (tB !== tA) return tB - tA;

                // 3️⃣ Nếu updated_at cũng bằng → giữ nguyên (stable sort)
                return 0;
            });
    }, [dailyStreakCampaignData]);

    return {
        data: dailyStreakCampaignData,
        dataDailyStreak,
        isLoading: campaignsIsLoading || dailyStreakCampaignIsLoading,
        error: campaignsError || dailyStreakCampaignError,
        milestones,
        mutate,
    };
};
